import { JEPTO_CARTESIAN_DATETIME_LABEL_FORMATS } from '../../../utils/highcharts-datetime.util';
import {
	buildTooltipComparisonSuffixHtml,
	resolveTooltipComparisonPoint,
} from '../../../utils/chart-comparison-tooltip.util';
import {
	buildCartesianAggregationLegendLabelHtml,
	chartHasComparisonLegendRows,
	resolveLegendSeriesUserOptions,
} from '../../../utils/chart-legend-scale.util';
import { resolveCssNumber } from '../../../utils/resolve-highcharts-css-colors.util';
import Highcharts from '../highcharts.config';

const formatNumber = (value: number) => {
	// add commas and make decimal optional, only show if it's not 0
	return value
		.toLocaleString('en-US', {
			minimumFractionDigits: 2,
			maximumFractionDigits: 2,
		})
		.replace(/\.00$/, '');
};

/** Legend swatch fades to ~20% alpha (`${color}33`); match that ratio on the plot fill. */
const AREA_FILL_FADE_BOTTOM_RATIO = 0.2;

/**
 * Vertical area fill gradient aligned with legend/tooltip swatches:
 * strong at the line, fading toward the baseline.
 */
export function createAreaChartFillColor(
	color: string,
	fillOpacity = 0.3
): Highcharts.GradientColorObject {
	const base = Highcharts.color(color);
	const topOpacity = fillOpacity;
	const bottomOpacity = fillOpacity * AREA_FILL_FADE_BOTTOM_RATIO;
	return {
		linearGradient: { x1: 0, y1: 0, x2: 0, y2: 1 },
		stops: [
			[0, base.setOpacity(topOpacity).get('rgba') as string],
			[1, base.setOpacity(bottomOpacity).get('rgba') as string],
		],
	};
}

export const areaChartDefaults: Highcharts.Options = {
	title: {
		text: undefined,
	},
	subtitle: {
		text: undefined,
	},
	series: [],
	chart: {
		type: 'area',
		animation: true,
		backgroundColor: 'transparent',
		style: {
			fontFamily: 'var(--font-family)',
		},
		spacing: [10, 20, 10, 20],
	},
	xAxis: {
		type: 'datetime',
		labels: {
			style: {
				color: 'var(--text-secondary-color)',
			},
		},
		tickmarkPlacement: 'on',
		gridLineWidth: 1,
		gridLineColor: 'var(--border-secondary-color)',
		lineColor: 'var(--border-primary-color)',
		tickColor: 'var(--border-primary-color)',
		dateTimeLabelFormats: JEPTO_CARTESIAN_DATETIME_LABEL_FORMATS,
	},
	yAxis: {
		title: {
			text: undefined,
		},
		labels: {
			style: {
				color: 'var(--text-primary-color)',
			},
		},
		gridLineColor: 'var(--border-secondary-color)',
		min: 0,
	},
	plotOptions: {
		area: {
			marker: {
				enabled: false,
				states: {
					hover: { enabled: true },
				},
			},
			fillOpacity: 0.3,
			lineWidth: 2,
		},
		series: {
			lineWidth: 2,
			dashStyle: 'Solid',
			opacity: 1,
			zIndex: 1,
		},
	},
	legend: {
		useHTML: true,
		labelFormatter: function () {
			const seriesRef = this as any;
			const options = resolveLegendSeriesUserOptions(seriesRef);
			const areaStyle = `background: linear-gradient(to bottom, ${this.color}, ${this.color}33)`;
			const chart = seriesRef.chart;

			return buildCartesianAggregationLegendLabelHtml({
				seriesName: this.name,
				swatchInlineStyle: areaStyle,
				aggregationValue: options.aggregationValue,
				comparisonAggregationValue: options.comparisonAggregationValue,
				comparisonData: options.comparisonData,
				formatNumber,
				comparisonRowEnabled: chartHasComparisonLegendRows(
					chart?.series
				),
			});
		},
		align: 'left',
		verticalAlign: 'top',
		itemStyle: {
			color: 'var(--text-primary-color)',
			fontWeight: 'normal',
			fontSize: 'var(--font-size-md)',
		},
		itemHoverStyle: {
			color: 'var(--text-primary-color)',
		},
		symbolRadius: 0,
		symbolWidth: 0,
		symbolHeight: 0,
		symbolPadding: 4,
		itemDistance: 12,
		layout: 'horizontal',
		alignColumns: true,
		itemMarginBottom: 2,
		navigation: {
			activeColor: 'var(--text-link-color)',
			inactiveColor: 'var(--text-secondary-color)',
			animation: true,
		},
	},
	tooltip: {
		outside: true,
		enabled: true,
		shared: true,
		useHTML: true,
		shadow: {
			color: 'var(--card-shadow-color)',
			opacity: 1,
			offsetX: 0,
			offsetY: 4,
			width: 12,
		},
		backgroundColor: 'var(--background-primary-color)',
		borderColor: 'var(--border-primary-color)',
		borderWidth: resolveCssNumber('--card-border-width', 1),
		borderRadius: resolveCssNumber('--card-border-radius', 6),
		formatter: function (this: any) {
			const points = this.points || [];
			const date = Highcharts.dateFormat('%b %d, %Y', this.x);
			let tooltipContent = `<div style="font-size: var(--font-size-xs); font-weight: var(--font-weight-regular); color: var(--text-secondary-color); margin-bottom: 5px;">${date}</div>`;

			const getAreaStyle = (point: any) => {
				return `background: linear-gradient(to bottom, ${point.series.color}, ${point.series.color}33)`;
			};

			points.forEach(point => {
				const seriesOptions = point.series.userOptions || {};
				const comparison = resolveTooltipComparisonPoint(
					seriesOptions.comparisonData,
					point.index ?? 0
				);

				const customOptions =
					point.series.options.custom || seriesOptions.custom || {};
				const inverseMetric = customOptions.inverseMetric;

				const comparisonHtml =
					comparison !== undefined
						? buildTooltipComparisonSuffixHtml(
								point.y,
								comparison,
								formatNumber,
								inverseMetric
							)
						: '';

				tooltipContent += `
                <div style="display: flex; align-items: center; margin-bottom: 5px;">
                    <div style="width: 3px; height: 30px; ${getAreaStyle(
						point
					)}; margin-right: 8px;"></div>
                    <div style="display: flex; flex-direction: column;">
                        <span style="color: var(--text-secondary-color); font-size: var(--font-size-xs); font-weight: var(--font-weight-regular);">${
							point.series.name
						}</span>
                        <div style="color: var(--text-primary-color); font-weight: var(--font-weight-regular); font-size: var(--font-size-sm)">
                            ${formatNumber(point.y)}${comparisonHtml}
                        </div>
                    </div>
                </div>`;
			});

			return `<div style="padding: 5px; background: var(--background-primary-color);">${tooltipContent}</div>`;
		},
		style: {
			color: 'var(--text-primary-color)',
			fontSize: 'var(--font-size-sm)',
		},
	},
	credits: {
		enabled: false,
	},
};
